import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { formatCurrency, formatNumber, type CurrencyOptions } from './formatCurrency';

interface CurrencyContextValue {
  currency: string;
  locale: string;
  format: (amount: number, opts?: CurrencyOptions) => string;
  formatNumber: (value: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({
  children,
  currency = 'INR',
  locale = 'en-IN'
}: {
  children: ReactNode;
  currency?: string;
  locale?: string;
}) {
  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      locale,
      format: (amount, opts) => formatCurrency(amount, { currency, locale, ...opts }),
      formatNumber: (v) => formatNumber(v, locale)
    }),
    [currency, locale]
  );
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Safe fallback so components work outside a provider (tests, storybook).
    return {
      currency: 'INR',
      locale: 'en-IN',
      format: (amount, opts) => formatCurrency(amount, opts),
      formatNumber: (v) => formatNumber(v)
    };
  }
  return ctx;
}
